import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

export type ThemeChoice = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

/** Chave também lida pelo script anti-flash do `index.html` — manter as duas iguais. */
export const THEME_STORAGE_KEY = "nora-theme";

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function writeChoice(choice: ThemeChoice) {
  try {
    if (choice === "system") localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // armazenamento indisponível (aba privada): a escolha vale só nesta sessão
  }
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

function subscribeSystem(onChange: () => void) {
  const mql = window.matchMedia(DARK_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

const systemIsDark = () => window.matchMedia(DARK_QUERY).matches;

/**
 * Cor da barra de status (metas `theme-color` do index.html, uma por esquema do sistema). Com tema forçado, as duas
 * passam a ter a cor desse tema; em "sistema", voltam aos valores originais (guardados em `data-default`).
 */
function syncThemeColor(choice: ThemeChoice) {
  const metas = [...document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')];
  const byScheme = (scheme: ResolvedTheme) =>
    metas.find((m) => (m.getAttribute("media") ?? "").includes(scheme))?.dataset.default ??
    metas.find((m) => (m.getAttribute("media") ?? "").includes(scheme))?.content;
  for (const m of metas) m.dataset.default ??= m.content;
  for (const m of metas) {
    m.content = (choice === "system" ? m.dataset.default : byScheme(choice)) ?? m.content;
  }
}

type ThemeContextValue = {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Preferência de tema (sistema/claro/escuro) aplicada como `data-theme` no `<html>`; "sistema" remove o atributo. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readChoice);
  const dark = useSyncExternalStore(subscribeSystem, systemIsDark, () => false);

  useEffect(() => {
    const root = document.documentElement;
    if (choice === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", choice);
    syncThemeColor(choice);
  }, [choice]);

  const setChoice = useCallback((c: ThemeChoice) => {
    writeChoice(c);
    setChoiceState(c);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved: choice === "system" ? (dark ? "dark" : "light") : choice, setChoice }),
    [choice, dark, setChoice],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme fora do ThemeProvider");
  return ctx;
}
